import React, { useState, useEffect, useCallback } from 'react';
import { TableSkeleton } from '../Skeleton';
import { adminService, ClientVersionRow } from '../../services/adminService';
import { getAppVersion } from '../../services/appVersion';
import { RefreshCw, Monitor } from 'lucide-react';
import { ADMIN_TAB_FILL, ADMIN_LIST_FILL } from './adminLayout';

function formatTimestamp(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}시간 전`;
  return d.toLocaleString('ko-KR');
}

export const ClientVersionList: React.FC = () => {
  const [rows, setRows] = useState<ClientVersionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminService.getClientVersions();
      setRows(data);
    } catch (err) {
      console.error('Failed to load client versions:', err);
      setLoadError(err instanceof Error ? err.message : '클라이언트 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getAppVersion().then(setCurrentVersion);
  }, [load]);

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* Actions bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">
          등록된 클라이언트 {rows.length}명
          {currentVersion && (
            <>
              <span className="mx-2">·</span>
              <span>현재 빌드 버전: <span className="font-medium text-slate-700">{currentVersion}</span></span>
            </>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          새로고침
        </button>
      </div>

      {/* Error banner */}
      {loadError && (
        <div className="mb-3 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded border border-brand-200 flex items-center justify-between">
          <span>{loadError}</span>
          <button onClick={load} className="text-brand-700 underline text-xs ml-2 shrink-0">
            다시 시도
          </button>
        </div>
      )}

      {/* Client list */}
      {loading && rows.length === 0 ? (
        <TableSkeleton rows={5} cols={4} />
      ) : !loadError && rows.length === 0 ? (
        <div className="text-center py-12 text-slate-400">기록된 클라이언트가 없습니다.</div>
      ) : (
        <div className={`space-y-2 ${ADMIN_LIST_FILL}`}>
          {rows.map(row => {
            const versionMatch = currentVersion && row.appVersion === currentVersion;
            return (
              <div
                key={row.userId}
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 bg-slate-50 rounded-lg flex items-center justify-center shrink-0">
                    <Monitor size={16} className="text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">
                        {row.userName || '(이름 없음)'}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          row.appVersion
                            ? versionMatch
                              ? 'bg-brand-50 text-brand-700'
                              : 'bg-slate-100 text-slate-600'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        v{row.appVersion || 'unknown'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      사번: {row.userId}
                      <span className="mx-1">·</span>
                      최근 접속: {formatTimestamp(row.lastSeenAt)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
